import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`A span is a single action within your application, such as a function call or vector database search.`}</p>
    <p><strong parentName="p">{`Event name`}</strong>{`: `}<inlineCode parentName="p">{`$ai_span`}</inlineCode></p>
    <h3 {...{
      "id": "core-properties"
    }}>{`Core properties`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Property`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_trace_id`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The trace ID (a UUID to group related AI events together)`}<br />{`Must contain only letters, numbers, and the following characters: `}<inlineCode parentName="td">{`-`}</inlineCode>{`, `}<inlineCode parentName="td">{`_`}</inlineCode>{`, `}<inlineCode parentName="td">{`~`}</inlineCode>{`, `}<inlineCode parentName="td">{`.`}</inlineCode>{`, `}<inlineCode parentName="td">{`@`}</inlineCode>{`, `}<inlineCode parentName="td">{`(`}</inlineCode>{`, `}<inlineCode parentName="td">{`)`}</inlineCode>{`, `}<inlineCode parentName="td">{`!`}</inlineCode>{`, `}<inlineCode parentName="td">{`'`}</inlineCode>{`, `}<inlineCode parentName="td">{`:`}</inlineCode>{`, `}<code>{`|`}</code><br />{`Example: `}<inlineCode parentName="td">{`d9222e05-8708-41b8-98ea-d4a21849e761`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_span_id`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`(Optional)`}</em>{` Unique identifier for this span`}<br />{`Example: `}<inlineCode parentName="td">{`bdf42359-9364-4db7-8958-c001f28c9255`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_span_name`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`(Optional)`}</em>{` The name of the span`}<br />{`Example: `}<inlineCode parentName="td">{`vector_search`}</inlineCode>{`, `}<inlineCode parentName="td">{`data_retrieval`}</inlineCode>{`, `}<inlineCode parentName="td">{`tool_call`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_parent_id`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`(Optional)`}</em>{` Parent ID for tree view grouping (`}<inlineCode parentName="td">{`trace_id`}</inlineCode>{` or another `}<inlineCode parentName="td">{`span_id`}</inlineCode>{`)`}<br />{`Example: `}<inlineCode parentName="td">{`537b7988-0186-494f-a313-77a5a8f7db26`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_input_state`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The input state of the span`}<br />{`Example: `}<inlineCode parentName="td">{`{"query": "search for documents about hedgehogs"}`}</inlineCode>{` or any JSON-serializable state`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_output_state`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The output state of the span`}<br />{`Example: `}<inlineCode parentName="td">{`{"results": ["doc1", "doc2"], "count": 2}`}</inlineCode>{` or any JSON-serializable state`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_latency`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`(Optional)`}</em>{` The latency of the span in seconds`}<br />{`Example: `}<inlineCode parentName="td">{`0.361`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_is_error`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`(Optional)`}</em>{` Boolean to indicate if the span encountered an error`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_error`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`(Optional)`}</em>{` The error message or object if the span failed`}<br />{`Example: `}<inlineCode parentName="td">{`{"message": "Connection timeout", "code": "TIMEOUT"}`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "example-api-call"
    }}>{`Example API call`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`curl -X POST "<ph_client_api_host>/i/v0/e/" \\
     -H "Content-Type: application/json" \\
     -d '{
         "api_key": "<ph_project_api_key>",
         "event": "$ai_span",
         "properties": {
             "distinct_id": "user_123",
             "$ai_trace_id": "d9222e05-8708-41b8-98ea-d4a21849e761",
             "$ai_input_state": {"query": "search for documents about hedgehogs", "filters": {"category": "animals"}},
             "$ai_output_state": {"results": [{"id": "doc_1", "content": "Hedgehogs are small mammals..."}, {"id": "doc_2", "content": "These nocturnal creatures..."}], "count": 2},
             "$ai_latency": 0.145,
             "$ai_span_name": "vector_search",
             "$ai_span_id": "bdf42359-9364-4db7-8958-c001f28c9255",
             "$ai_parent_id": "537b7988-0186-494f-a313-77a5a8f7db26",
             "$ai_is_error": false
         },
         "timestamp": "2025-01-30T12:00:00Z"
     }'
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      